import { FunctionComponent, KeyboardEvent, useCallback, useState } from 'react'
import { Input } from '../../shared'
import { TooltipIconButton } from '../TooltipIconButton'
import { useRenameConversation } from '../../../hooks/queries/statsAssistant'
import { EditableConversationTitleProps } from './EditableConversationTitle.model'
import styles from './EditableConversationTitle.module.scss'

export const EditableConversationTitle: FunctionComponent<EditableConversationTitleProps> = ({
  ulid,
  title,
  className,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(title)
  const renameMutation = useRenameConversation()

  const startEdit = useCallback(() => {
    if (!ulid) return
    setValue(title)
    setIsEditing(true)
  }, [title, ulid])

  const cancel = useCallback(() => {
    setIsEditing(false)
    setValue(title)
  }, [title])

  const commit = useCallback(async () => {
    const trimmed = value.trim()
    if (!ulid || !trimmed || trimmed === title) {
      cancel()
      return
    }
    await renameMutation.mutateAsync({ ulid, title: trimmed })
    setIsEditing(false)
  }, [cancel, renameMutation, title, ulid, value])

  // Input n'expose que onKeyPress, qui ne reçoit pas Échap.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        void commit()
      }
      if (event.key === 'Escape') cancel()
    },
    [cancel, commit],
  )

  if (!ulid) {
    return <span className={className}>{title}</span>
  }

  if (isEditing) {
    return (
      <div className={styles.input} onKeyDown={handleKeyDown}>
        <Input
          name="stats_assistant_conversation_title"
          height="small"
          colorPreset="light"
          value={value}
          autofocus
          maxLength={80}
          customClassName={styles.field}
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => void commit()}
          testId="input-stats_assistant-conversation_title"
        />
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <span className={className}>{title}</span>
      <TooltipIconButton
        icon="pencil"
        label="Renommer la conversation"
        onClick={startEdit}
        testId="button-stats_assistant-conversation_title-edit"
      />
    </div>
  )
}
